import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { SearchQuery, SearchType } from "hooks/search/types";
import { Control, useController, useFormContext } from "react-hook-form";
import { useFeatureFlags } from "contexts/FeatureFlagContext";
import { defaultFilterValues } from "../filters/constants";

interface SearchTypeDropdownProps {
  control: Control<SearchQuery>;
  onTypeChange?: (from: SearchType, to: SearchType) => void;
}

export const SearchTypeDropdown = ({
  control,
  onTypeChange,
}: SearchTypeDropdownProps) => {
  const { t } = useTranslation(["books"]);
  const { features } = useFeatureFlags();

  const form = useFormContext();
  const { field } = useController({ name: "type", control });

  const dropdownDescriptions = useMemo(
    () => ({
      [SearchType.QUICK]: t("books:search.backlistTypes.quick"),
      [SearchType.ISBN]: t("books:search.backlistTypes.isbn"),
      [SearchType.SEMANTIC]: t("books:search.backlistTypes.semantic"),
      [SearchType.HYBRID]: t("books:search.backlistTypes.hybrid"),
    }),
    [t],
  );

  const onValueChange = useCallback(
    (searchType: SearchType) => {
      if (searchType === field.value) return;
      onTypeChange?.(field.value, searchType);

      if (searchType === SearchType.ISBN || field.value === SearchType.ISBN) {
        // Reset query and filter when switching to/from ISBN search type
        // as ISBN search requires exact matches and doesn't support filtering
        form.reset({
          ...form.getValues(),
          type: searchType,
          query: "",
          pageSize: null,
          filters: defaultFilterValues,
        });
      } else {
        form.reset({
          ...form.getValues(),
          type: searchType,
          pageSize: null,
        });
      }
    },
    [field, form, onTypeChange],
  );

  const options = useMemo(() => {
    return Object.entries(SearchType)
      .filter(
        ([, type]) =>
          type !== SearchType.HYBRID || features.useElasticsearchHybridSearch,
      )
      .map(([key, type]) => (
        <SelectItem
          key={key}
          value={type}
          data-testid={`search-dropdown-item-${key}`}
          analytics-id="button_backlist_input_type"
        >
          <p>{dropdownDescriptions[type]}</p>
        </SelectItem>
      ));
  }, [dropdownDescriptions, features.useElasticsearchHybridSearch]);

  return (
    <Select value={field.value} onValueChange={onValueChange}>
      <SelectTrigger
        className="data-[size=default]:h-full border-secondary-300 focus-visible:border-secondary-300 focus-visible:ring-0 rounded-lg rounded-e-none bg-secondary-50 min-w-40"
        data-testid="search-dropdown"
      >
        <SelectValue placeholder="Search type" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>{options}</SelectGroup>
      </SelectContent>
    </Select>
  );
};
